import alabasterapp from '../../assets/alabasterapp.png'
import doctorsNg from '../../assets/doctors-ng.png'
import image2 from '../../assets/project2.jpeg'
import image3 from '../../assets/project3.jpeg'
import image7 from '../../assets/project7.png'
import image8 from '../../assets/project8.png'
import campPrj from '../../assets/camp-image.jpg'
import theProfast from '../../assets/theprofast.jpg'
import natureborn from '../../assets/natureborn.jpg'

export const GITHUB_PROFILE = 'https://github.com/WilsonTomisin'

/*
 * Shared source of truth for every "selected work" layout.
 *
 * `blurb` is the one-line description used by the list layouts.
 *
 * `caseStudy` is optional — only projects that have one get a /projects/:slug
 * page, and the list rows link there instead of straight out to the live site.
 * Every field inside it is optional too: CaseStudy.jsx skips any section that
 * is empty, so a study can be filled in a piece at a time without the page
 * ever looking broken.
 *
 *   role      — what you were hired as
 *   timeline  — when, and for how long
 *   summary   — one paragraph of positioning (falls back to `blurb`)
 *   problem   — what the client/product was actually up against
 *   approach  — array of strings: what you built, in plain terms
 *   decisions — array of { title, body, link? }: the calls you made and the
 *               trade-off. `link` is optional { label, url } — use it when
 *               you've written the decision up somewhere at length.
 *   outcome   — what shipping it changed
 */
export const projects = [
  {
    slug: 'alabasterapp',
    path: alabasterapp,
    title: 'Alabasterapp LTD',
    liveURL: 'https://alabasterapp.com',
    repo: 'https://github.com/WilsonTomisin',
    stack: ['React', 'Tailwind', 'Express', 'WebSockets', 'Payment Integration'],
    year: '2025',
    isPublic: false,
    blurb: 'E-commerce platform for shopping authentic goods.',
    caseStudy: {
      role: 'Frontend Engineer',
      timeline: '2025',
      // DRAFT — corrected against your notes. Still verify the payment
      // paragraph; the rest follows what you described.
      summary:
        'Alabasterapp is an e-commerce platform built around a single promise: everything sold on it is authentic. I worked on the storefront, and on the two things a plain React app could not do on its own — server-rendering product metadata so shared links preview properly, and a live chat channel for settling disputes over an item.',
      problem:
        'Everything about the platform rests on a buyer believing a listing is genuine, and two things worked against that. Product links get shared constantly — WhatsApp, X, group chats — and a client-rendered storefront hands a social crawler an empty app shell, so a link meant to sell one specific product arrived as a blank preview card. And when a buyer did dispute an item, resolving it meant email threads and screenshots, with an admin trying to reconstruct the argument after the fact.',
      approach: [
        'Built the customer-facing storefront in React and Tailwind — catalogue, product pages, search and cart.',
        'Put an Express layer in front of the app that renders per-product metadata on the server, so a crawler following a shared product link gets real Open Graph tags — title, image, price — instead of the shell.',
        'Built a WebSocket chat in the front end that puts an admin in the room with the parties to a product dispute, so it gets resolved as a conversation instead of a chain of forwarded emails.',
        "Integrated the third-party payment gateway into checkout, with an order treated as paid only once the gateway's verified webhook confirmed it — never on the browser redirect alone.",
      ],
      decisions: [
        {
          title: 'Render the metadata, don’t migrate the app',
          body:
            'The obvious fix for empty link previews is to move the whole storefront onto a framework that renders on the server. That is a migration measured in weeks, to solve a problem that only affects what crawlers see — real users were already getting the full app. An Express layer that renders metadata for bot requests fixed the previews without anyone touching the React codebase.',
          link: {
            label: 'I wrote this one up in full',
            url: 'https://medium.com/@tommywilson972/stop-migrating-to-next-js-how-i-rendered-social-media-metadata-with-express-7b6a07d14b6e',
          },
        },
        {
          title: 'A conversation, not a ticket queue',
          body:
            'A dispute over whether an item is authentic is genuinely a conversation: one side has photos, the other has an explanation, and an admin has to weigh both. A ticket form flattens that into a queue and leaves the admin chasing context across replies. Putting everyone in a live thread keeps the evidence and the decision in one place, and lets the admin close it out while both parties are still there.',
        },
        {
          title: 'Trust the webhook, not the redirect',
          body:
            'The quick way to confirm a payment is to believe the browser when the gateway redirects back with a success flag. It is also the easiest thing in the flow to forge, and it silently fails every time someone closes the tab mid-payment. Settling only on the gateway’s server-to-server webhook costs a moment of “pending” in the UI and removes an entire class of way it can go wrong.',
        },
      ],
      // TODO(wilson): I have no visibility into what launch actually changed —
      // link-share conversion, dispute resolution time, order volume. Your words.
      outcome: '',
    },
  },
  {
    slug: 'doctors-ng',
    path: doctorsNg,
    title: 'Doctors.ng',
    liveURL: 'https://doctors.ng',
    repo: 'https://github.com/WilsonTomisin',
    stack: ['Next.js', 'Tailwind', 'AI integration'],
    year: '2025',
    isPublic: false,
    blurb:
      'Verified professional identity and directory for Nigerian doctors — connects patients to doctors, with an AI symptom checker.',
    caseStudy: {
      role: 'Frontend Engineer',
      timeline: '2025',
      // DRAFT — written from the blurb and stack. The SSR/sharing claims below
      // are inferences; cut them if the profiles aren't actually server-rendered.
      summary:
        'Doctors.ng gives Nigerian doctors a verified professional identity, and gives patients a trustworthy way to find them. I worked on the front end — the directory and profile pages people search, and the AI symptom checker that helps them work out who they need to see.',
      problem:
        "Anyone can claim to be a doctor online, and patients looking for care have had to fall back on word of mouth or whatever a search engine happened to surface. The product had to do two things at once: make a doctor's verification visible and credible at a glance, and give a patient somewhere to start when they don't yet know what kind of specialist they need — without ever behaving like a diagnosis.",
      approach: [
        'Built the verified doctor profiles and the searchable directory in Next.js and Tailwind.',
        'Server-rendered profile pages so each doctor is indexable by name and produces a real preview card when the link is shared.',
        'Built the AI symptom checker interface — collecting symptoms conversationally, then turning the result into a recommended specialty and a shortlist of verified doctors.',
        'Designed the verification signals so a verified profile reads differently from an unverified one at a glance, without needing a paragraph of explanation.',
        "Handled the model's failure modes in the UI: uncertainty, refusals and vague input all resolve to seeing a real doctor, never to a dead end.",
      ],
      decisions: [
        {
          title: 'Route, never diagnose',
          body:
            'A symptom checker that names a condition feels far more impressive and is far more dangerous. The output is always a specialty and a list of doctors, never a diagnosis, and the copy never implies the model has assessed anyone. It is a deliberately less clever product, and the only version of it I would ship in healthcare.',
        },
        {
          title: 'Render profiles on the server',
          body:
            'A doctor’s profile is the unit that actually gets searched for and shared. Rendering it server-side means it can be indexed by the doctor’s name and unfurls into a proper preview when someone passes the link around — which is how most of these links travel. Client-side rendering would have been simpler to build and invisible to both.',
        },
      ],
      // TODO(wilson): sign-ups, verified doctors onboarded, symptom-checker
      // usage — whatever you can honestly say. Leave blank rather than guess.
      outcome: '',
    },
  },
  {
    slug: 'natureborn-africa',
    path: natureborn,
    title: 'Natureborn Africa',
    liveURL: 'https://natureborn.africa',
    repo: 'https://github.com/WilsonTomisin',
    stack: ['React', 'Tailwind'],
    year: '2024',
    isPublic: false,
    blurb: 'Site for a concierge and hospitality service.',
  },
  {
    slug: 'access-medical-supplies',
    path: image8,
    title: 'Access Medical Supplies',
    liveURL: 'https://accessmedsupplies.com',
    repo: 'https://github.com/WilsonTomisin',
    stack: ['Next.js', 'Tailwind'],
    year: '2024',
    isPublic: false,
    blurb: 'E-commerce catalog for a medical supply distributor.',
  },
  {
    slug: 'theprofast-logistics',
    path: theProfast,
    title: 'TheProfast Logistics',
    liveURL: 'https://theprofast.netlify.app/',
    repo: 'https://github.com/WilsonTomisin',
    stack: ['Next.js', 'Tailwind'],
    year: '2024',
    isPublic: false,
    blurb: 'Booking and tracking front-end for a logistics company.',
  },
  {
    slug: 'kuttinedge-resources',
    path: image7,
    title: 'Kuttinedge Resources',
    liveURL: 'https://kuttinedgeresources.com/',
    repo: 'https://github.com/WilsonTomisin',
    stack: ['React', 'Tailwind'],
    year: '2023',
    isPublic: false,
    blurb: 'Site for a study-abroad and student visa agency.',
  },
  {
    slug: 'camping-agency',
    path: campPrj,
    title: 'Camping Agency',
    liveURL: 'https://camp-site-pied.vercel.app/',
    repo: 'https://github.com/WilsonTomisin/camp-site',
    stack: ['React', 'Tailwind'],
    year: '2023',
    isPublic: true,
    blurb: 'Concept landing page for an outdoor adventure agency.',
  },
  {
    slug: 'photo-gallery',
    path: image2,
    title: 'Photo Gallery',
    liveURL: 'https://wilsontomisin-imagegallery.netlify.app/',
    repo: 'https://github.com/WilsonTomisin/hng-task3',
    stack: ['React', 'Firebase'],
    year: '2023',
    isPublic: true,
    blurb: 'Image upload and gallery app backed by Firebase storage.',
  },
  {
    slug: 'movie-box',
    path: image3,
    title: 'Movie Box',
    liveURL: 'https://wilsontomisin-moviebox.netlify.app/',
    repo: 'https://github.com/WilsonTomisin/hng-task2',
    stack: ['React', 'TMDB API'],
    year: '2023',
    isPublic: true,
    blurb: 'Movie discovery app powered by the TMDB API.',
  },
]

/** A study is only worth linking to once it has real content in it. */
export const hasCaseStudy = (p) =>
  Boolean(p.caseStudy && (p.caseStudy.problem || p.caseStudy.approach?.length))

export const getProject = (slug) => projects.find((p) => p.slug === slug)
